import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, SegmentedControl } from '@mantine/core';
import { AlertBanner } from '@shipstation/client-common/components/alert-banner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { emptyArray } from 'ui-components/default-props/empty';

import { FulfillmentPlanFulfillmentType } from '../../../../../../enums/fulfillment-plan-fulfillment-type';
import Global from '../../../../../../store/global-selectors';
import { selectLtlHasFulfillmentTypeError, selectLtlIsFulfillmentTypeChanging } from '../../../../../../store/ltl/core/ltl.core.selectors';
import { setFulfillmentTypeFreight, setFulfillmentTypeParcel } from '../../../../../../store/ltl/ltl.actions';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents, ShippingMode, ShippingModeChangeScope } from '../../../../../../pages/ltl-freight/tracking-events';

import { messages } from './messages';
import { FlexRow, Gap } from 'ui-components/components/flex';
import styles from './shipment-type-toggle.module.css';
import { useEnableLtlFreight } from '../../../../../../feature-flags/hooks/use-enable-ltl-freight';
import { useCanSwitchToFreight } from '../../../../../../hooks/freight/use-can-switch-to-freight';
import { messages as canSwitchToFreightMessages } from '../../../../../../hooks/freight/use-can-switch-to-freight.messages';

const FREIGHT_UNAVAILABLE_BANNER_CLASS_NAMES = { root: styles['freight-unavailable-banner'] };

type ShipmentTypeToggleProps = {
  fulfillmentPlanId: string | undefined;
  className?: string;
};

export const ShipmentTypeToggle = ({ fulfillmentPlanId, className }: ShipmentTypeToggleProps) => {
  const isLtlFreightEnabled = useEnableLtlFreight();
  const intl = useIntl();
  const dispatch = useDispatch();

  const fulfillmentType = useSelector((state) => Global.selectEntitiesFulfillmentPlanFulfillmentType(state, { fulfillmentPlanId }));
  const isLoading = useSelector(selectLtlIsFulfillmentTypeChanging);
  const hasError = useSelector(selectLtlHasFulfillmentTypeError);
  const fulfillmentPlanIds = useMemo(() => (fulfillmentPlanId ? [fulfillmentPlanId] : emptyArray), [fulfillmentPlanId]);
  const canSwitchToFreight = useCanSwitchToFreight(fulfillmentPlanIds);

  const packageLabel = intl.formatMessage(messages.packageOption);
  const freightLabel = intl.formatMessage(messages.freightOption);

  const reduxDerivedValue = fulfillmentType === FulfillmentPlanFulfillmentType.FreightLtl ? freightLabel : packageLabel;

  const [activeValue, setActiveValue] = useState(reduxDerivedValue);
  const [loadingValue, setLoadingValue] = useState<string | null>(null);

  useEffect(() => {
    const isFreight = fulfillmentPlanId && fulfillmentType === FulfillmentPlanFulfillmentType.FreightLtl;
    if (!isLtlFreightEnabled && isFreight) {
      dispatch(setFulfillmentTypeParcel(fulfillmentPlanId));
    }
  }, [dispatch, fulfillmentPlanId, fulfillmentType, isLtlFreightEnabled]);

  useEffect(() => {
    if (!isLoading) {
      setActiveValue(reduxDerivedValue);
      setLoadingValue(null);
    }
  }, [isLoading, reduxDerivedValue]);

  useEffect(() => {
    if (hasError) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.updateFailed),
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [hasError, intl]);

  const handleChange = useCallback(
    (value: string) => {
      if (value === activeValue || !fulfillmentPlanId) return;
      setActiveValue(value);
      setLoadingValue(value);
      const isFreight = value === freightLabel;
      if (isFreight) {
        dispatch(setFulfillmentTypeFreight(fulfillmentPlanId));
      } else {
        dispatch(setFulfillmentTypeParcel(fulfillmentPlanId));
      }
      Segment.trackEvent(LtlFreightTrackingEvents.SHIPPING_MODE_CHANGED, {
        shipping_mode: isFreight ? ShippingMode.Freight : ShippingMode.Parcel,
        scope: ShippingModeChangeScope.Single,
        fulfillment_plan_id: fulfillmentPlanId,
      });
    },
    [activeValue, dispatch, freightLabel, fulfillmentPlanId],
  );

  const makeLabel = (label: string) =>
    loadingValue === label ? (
      <FlexRow className={styles['loading-label']} gap={Gap.Space4}>
        <FontAwesomeIcon icon={faSpinner} spin />
        {label}
      </FlexRow>
    ) : (
      label
    );

  if (!isLtlFreightEnabled) {
    return null;
  }

  const isFreightBlocked = !canSwitchToFreight && activeValue !== freightLabel;

  return (
    <Flex className={className} direction='column' gap='xs'>
      <SegmentedControl
        className={styles.container}
        data={[
          { value: packageLabel, label: makeLabel(packageLabel), disabled: isLoading },
          { value: freightLabel, label: makeLabel(freightLabel), disabled: isLoading || isFreightBlocked },
        ]}
        fullWidth
        onChange={handleChange}
        value={activeValue}
      />
      {isFreightBlocked && (
        <AlertBanner
          classNames={FREIGHT_UNAVAILABLE_BANNER_CLASS_NAMES}
          description={intl.formatMessage(canSwitchToFreightMessages.freightUnavailableOutsideUs)}
          stacked={false}
          status='inform'
        />
      )}
    </Flex>
  );
};
